import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from '@app/config/i18n';
import TableHeaderRow from '@app/components/molecules/TableHeader/TableHeaderRow';
import TablePagination from '@app/components/molecules/TablePagination/TablePagination';
import useTablePagination from '@app/hooks/useTablePagination';
import SubmissionsTableToolbar from './SubmissionsTableToolbar';
import { useSubmissionsListTable } from './useSubmissionsListTable';

export default function SubmissionsListTable({
  opportunity,
  submissionDataArray,
  paginatorInfo,
  onPageChange,
  onRowsPerPageChange,
  applicationSubmissionFilters,
  filters,
  onFilterChange,
  loading,
  onDelete,
  onSendReminder,
  onSendEmail,
  onAssignReviewers,
}) {
  const { t } = useTranslation(['common']);
  const navigationQuery = React.useMemo(
    () => ({
      page: paginatorInfo?.currentPage || 1,
      perPage: paginatorInfo?.perPage || 10,
      listFilters: JSON.stringify(filters || {}),
    }),
    [filters, paginatorInfo?.currentPage, paginatorInfo?.perPage]
  );

  const {
    // State
    order,
    orderBy,
    selected,
    columns,

    // Data
    tableRowsData,

    // Handlers
    handleRequestSort,
    handleSelectAllClick,
    handleRowClick,
    handleToggleColumn,

    // Utilities
    isSelected,
    renderCellContent,
    renderViewAction,
  } = useSubmissionsListTable({
    opportunity,
    submissionDataArray,
    navigationQuery,
  });

  // Use filters from parent component

  const totalCount = paginatorInfo?.total || tableRowsData?.length;

  const handleFilterChange = (key, value) => {

    if (onFilterChange) {
      onFilterChange(key, value);
    }
  };
  const handleSearchChange = (value) => {
    handleFilterChange('q', value);
  };

  const handleSendReminder = () => {
    if (onSendReminder) {
      onSendReminder(selected);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(selected);
    }
  };

  const handleSendEmail = () => {
    if (onSendEmail) {
      console.log('selected', selected);
      onSendEmail(selected);
    }
  };

  const handleAssignReviewers = () => {
    if (onAssignReviewers) {
      onAssignReviewers(selected);
    }
  };

  const {
    page,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
  } = useTablePagination({
    initialPage: 0,
    initialRowsPerPage: 10,
    totalItems: totalCount,
    onPageChange,
    onRowsPerPageChange,
    isServerSide: !!paginatorInfo,
    externalPage: paginatorInfo?.currentPage ? paginatorInfo.currentPage - 1 : 0,
    externalRowsPerPage: paginatorInfo?.perPage || 10,
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        sx={{
          width: '100%',
          mb: 2,
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.05)',
        }}
      >
        <SubmissionsTableToolbar
          rowsSelected={Object.keys(selected).length}
          columns={columns}
          onToggleColumn={handleToggleColumn}
          filters={filters}
          onFilterChange={handleFilterChange}
          onSearchChange={handleSearchChange}
          title={t('common:Submissions')}
          applicationSubmissionFilters={applicationSubmissionFilters}
          opportunity={opportunity}
          onDelete={handleDelete}
          onSendReminder={handleSendReminder}
          onSendEmail={handleSendEmail}
          onAssignReviewers={handleAssignReviewers}
        />
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 1,
              }}
              className="table-loading-overlay"
            >
              <CircularProgress />
            </Box>
          )}
          <TableContainer>
            <Table
              sx={{
                minWidth: 750,
                '& .MuiTableHead-root': {
                  backgroundColor: '#F5F7FA',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
              aria-labelledby="tableTitle"
              size="small"
              key={
                paginatorInfo
                  ? `page-${paginatorInfo.currentPage}-size-${paginatorInfo.perPage}`
                  : 'client-side'
              }
            >
              <TableHeaderRow
                numSelected={Object.keys(selected).length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={totalCount}
                columns={columns}
              />
              <TableBody>
                {tableRowsData?.length > 0 ? (
                  tableRowsData.map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.id}
                        selected={isItemSelected}
                        sx={{
                          cursor: 'pointer',
                          height: '52px',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                          '&.Mui-selected:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.12)',
                          },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            onClick={(event) => handleRowClick(event, row)}
                            color="primary"
                            checked={isItemSelected}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                            size="small"
                          />
                        </TableCell>

                        {columns.map((headCell) => {
                          if (headCell.id === 'select') return null;
                          if (!headCell.isVisible) return null;

                          return (
                            <TableCell
                              key={`${row.id}-${headCell.id}`}
                              align={headCell.numeric ? 'right' : 'left'}
                              padding={headCell.disablePadding ? 'none' : 'normal'}
                            >
                              {renderCellContent(row, headCell)}
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          {renderViewAction(row, t, opportunity)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      <Typography variant="body1">
                        {loading ? '' : t('common:No submissions found')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <TablePagination
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          onPageChange={handlePageChange}
          totalItems={totalCount}
        />
      </Paper>
    </Box>
  );
}

SubmissionsListTable.propTypes = {
  opportunity: PropTypes.object.isRequired,
  submissionDataArray: PropTypes.array,
  paginatorInfo: PropTypes.object,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  applicationSubmissionFilters: PropTypes.object,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func,
  loading: PropTypes.bool,
  onDelete: PropTypes.func,
  onSendReminder: PropTypes.func,
  onSendEmail: PropTypes.func,
  onAssignReviewers: PropTypes.func,
};

SubmissionsListTable.defaultProps = {
  submissionDataArray: [],
  paginatorInfo: null,
  onPageChange: null,
  onRowsPerPageChange: null,
  applicationSubmissionFilters: null,
  filters: { status: [], q: '' },
  onFilterChange: null,
  loading: false,
  onDelete: null,
  onSendReminder: null,
  onSendEmail: null,
  onAssignReviewers: null,
};
